import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../store/auth';
import { messageHandler } from '../services/message-handler.service';
import api from '../lib/api';
import PageHeader from '../components/ui/PageHeader';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import LicenseInfo from '../components/settings/LicenseInfo';
import PaymentHistory from '../components/settings/PaymentHistory';
import AboutInfo from '../components/settings/AboutInfo';
import TwoFactorSettings from '../components/settings/TwoFactorSettings';

function Settings() {
  const [activeTab, setActiveTab] = useState('licenca');
  const queryClient = useQueryClient();

  // Queries
  const { data: subscription } = useQuery({
    queryKey: ['subscription'],
    queryFn: async () => {
      const response = await api.get('/subscriptions/users');
      return response.data[0] || null;
    },
  });

  // Get plans with referred_by parameter if user has a referred_by value
  const { data: plans } = useQuery({
    queryKey: ['subscription-plans', subscription?.referred_by],
    queryFn: async () => {
      // If user was referred, pass the referrer's ID to get discounted plans
      const params = subscription?.referred_by ? { referred_by: subscription.referred_by } : {};
      const response = await api.get('/subscriptions/plans', { params });
      return response.data;
    },
    enabled: !!subscription,
  });

  const { data: paymentResponse, isLoading: isLoadingPayments } = useQuery({
    queryKey: ['payments'],
    queryFn: async () => {
      const response = await api.get('/subscriptions/payments');
      return response.data;
    },
  });

  const payments = paymentResponse?.data || [];

  // Mutations
  const cancelSubscriptionMutation = useMutation({
    mutationFn: async (id: number) => {
      await api.post(`/subscriptions/users/${id}/cancel`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      messageHandler.success('Assinatura cancelada com sucesso');
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const reactivateSubscriptionMutation = useMutation({
    mutationFn: async (id: number) => {
      await api.post(`/subscriptions/users/${id}/reactivate`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      messageHandler.success('Assinatura reativada com sucesso');
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  // Handlers
  const handleCancelSubscription = () => {
    if (!subscription) return;

    messageHandler.confirm(
      'Cancelar Assinatura',
      'Tem certeza que deseja cancelar sua assinatura? Esta ação não pode ser desfeita.',
      () => cancelSubscriptionMutation.mutate(subscription.id)
    );
  };

  const handleReactivateSubscription = () => {
    if (!subscription) return;

    messageHandler.confirm(
      'Reativar Assinatura',
      `Tem certeza que deseja reativar sua assinatura? Uma nova cobrança de ${new Intl.NumberFormat(
        'pt-BR',
        { style: 'currency', currency: 'BRL' }
      ).format(Number(subscription.price))} será gerada imediatamente após a reativação.`,
      () => reactivateSubscriptionMutation.mutate(subscription.id)
    );
  };

  const tabs = [
    { id: 'licenca', label: 'Licença' },
    { id: 'faturamento', label: 'Faturamento' },
    { id: 'seguranca', label: 'Segurança' },
    { id: 'sobre', label: 'Sobre' },
  ];

  // Loading state
  if (isLoadingPayments) {
    return (
      <div className="py-6">
        <div className="px-4 sm:px-6 lg:px-8">
          <PageHeader
            title="Configurações"
            description="Gerencie suas configurações e informações pessoais."
          />
          <div className="flex justify-center items-center h-64">
            <LoadingSpinner size="lg" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-6">
      <div className="px-4 sm:px-6 lg:px-8">
        <PageHeader
          title="Configurações"
          description="Gerencie suas configurações e informações pessoais."
        />

        <div className="mt-6 border-b border-gray-200">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                  ${
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="mt-6">
          {activeTab === 'licenca' && (
            <LicenseInfo
              subscription={subscription}
              plans={plans}
              onCancelSubscription={handleCancelSubscription}
              onReactivateSubscription={handleReactivateSubscription}
            />
          )}

          {activeTab === 'faturamento' && <PaymentHistory payments={payments} />}

          {activeTab === 'seguranca' && (
            <div className="space-y-6">
              <TwoFactorSettings />
            </div>
          )}

          {activeTab === 'sobre' && <AboutInfo />}
        </div>
      </div>
    </div>
  );
}

export default Settings;
